import React, { useEffect, useState, useLayoutEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import Pages from "../route/Index";
import Sidebar from "./sidebar/Sidebar";
import Head from "./head/Head";
import Header from "./header/Header";
import Footer from "./footer/Footer";
import classNames from "classnames";
import { connectWithAuth, socketIO } from "services/socket";
import { MessagesContext } from "contexts/messagesContext";
import { useCookies } from "react-cookie";
import { servicePutUsersOnline, servicePutUsersOffline } from "services/users";
import { ToastContainer, toast } from "react-toastify";
import {
  onMessageListener,
  requestForFirebaseToken,
  subscribeFirebaseToken,
  unsubscribeFirebaseToken,
} from "services/firebase";
import { Icon } from "components/Component";
import lonceng4 from "assets/sound/lonceng4.mp3";

export const playSound = () => {
  try {
    const sound = new Audio(lonceng4);
    sound.play();
  } catch (error) {
    console.log(error);
  }
};

const MAX_SEEN_MESSAGE_IDS = 5000;
const MAX_LIVECHAT_EVENTS = 200;

const Layout = () => {
  const [notification, setNotification] = useState({ title: "", body: "" });
  const [cookies, setCookie, removeCookie] = useCookies(["user"]);
  const roleFunctions = JSON.parse(localStorage.getItem("permission") || "{}")?.["chats"]?.access;
  const [mobileView, setMobileView] = useState();
  const [visibility, setVisibility] = useState(false);
  const [statusOnlineUsers, setStatusOnlineUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newTicket, setNewTicket] = useState();
  const [statusMessages, setStatusMessages] = useState([]);
  const [newSales, setNewSales] = useState();
  const [livechatEvents, setLivechatEvents] = useState([]);
  const [livechatConnects, setLivechatConnects] = useState(0);
  const livechatSeqRef = useRef(0);
  const [themeState] = useState({
    main: "default",
    sidebar: "white",
    header: "white",
    skin: "light",
  });
  const seenMessageIdsRef = useRef(new Set());
  let history = useHistory();

  useEffect(() => {
    document.body.className = `nk-body bg-lighter npc-default has-sidebar no-touch nk-nio-theme ${
      themeState.skin === "dark" ? "dark-mode" : ""
    }`;
  }, [themeState.skin]);

  useEffect(() => {
    if (cookies.SIDikey && socketIO) {
      initializeFirebaseToken();
      const cleanupFn = initializeSocket();
      return () => {
        // Make sure to call the cleanup function
        if (typeof cleanupFn === "function") cleanupFn();
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookies.SIDikey, socketIO]); // Dependencies to watch for changes

  async function initializeSocket() {
    try {
      if (cookies.SIDikey) {
        await connectWithAuth(cookies.SIDikey);
        // Connect to socket
        socketIO.connect();

        const socketListener = (msg_raw) => {
          if (!msg_raw) return;
          const env_str = process.env.REACT_APP_SENTRY_ENVIRONMENT ?? "production";

          // Your existing socket message handling logic
          if (env_str === "development" || env_str === "staging") {
            console.log("SOCKET MESSAGE :", msg_raw);
          }

          if (msg_raw.type && msg_raw.type === "signOut" && msg_raw.id && msg_raw.id === cookies.id) {
            // MULTI DEVICE OFFLINE
            servicePutUsersOffline(cookies.SIDikey, cookies.id);
            if (localStorage.getItem("firebaseToken")) {
              unsubscribeFirebaseToken(cookies.SIDikey, localStorage.getItem("firebaseToken"));
            }
            removeCookie("SIDikey");
            localStorage.removeItem("firebaseToken");
            localStorage.removeItem("getFirebaseTokenAt");
            localStorage.removeItem("permission");
            localStorage.removeItem("userDetail");
            localStorage.removeItem("loginAt");
            history.push("/auth-login");
            return;
          }

          if (msg_raw.type === "LIVECHAT_EVENT") {
            livechatSeqRef.current += 1;
            const event = { ...msg_raw, seq: livechatSeqRef.current };
            setLivechatEvents((events) => [...events.slice(1 - MAX_LIVECHAT_EVENTS), event]);
            return;
          }

          if (msg_raw.type === "UNOPENED_CHAT_SUMMARY") {
            setNotification({ title: msg_raw.title, body: msg_raw.body, tab: "unopened" });
            return;
          }

          if (msg_raw.type === "USER_STATUS_UPDATED") {
            const userId = msg_raw.userId;
            const newStatus = msg_raw.status;

            if (userId === cookies.id) {
              try {
                const userDetail = JSON.parse(localStorage.getItem("userDetail") || "{}");
                userDetail.status = newStatus;
                localStorage.setItem("userDetail", JSON.stringify(userDetail));
                window.dispatchEvent(new Event("user-status-changed"));
              } catch (e) {}
            }

            setStatusOnlineUsers((users) => {
              const exists = users.find((element) => element.id === userId);
              if (exists) {
                return users.map((el) => (el.id === userId ? { ...el, status: newStatus } : el));
              } else {
                return [...users, { id: userId, status: newStatus, email: msg_raw.email }];
              }
            });
            return;
          }

          if ("sign" in msg_raw) {
            // LOGIN AND SIGN IN : ONLINE USERS
            msg_raw.socketType = "SIGNIN";
            if (!!statusOnlineUsers.find((element) => element.id === msg_raw.id)) {
              const onlineStatus = statusOnlineUsers;
              const osNew = [];
              onlineStatus.forEach((el) => {
                if (el.id === msg_raw.id) {
                  el.status = msg_raw.status;
                }
                osNew.push(el);
              });
              setStatusOnlineUsers((users) => [...osNew]);
            } else {
              setStatusOnlineUsers((users) => [...users, msg_raw]);
            }
            return;
          }

          if ("fromMe" in msg_raw) {
            msg_raw.socketType = "MESSAGE STATUS";
            const dedupeKey = msg_raw?.id;
            if (dedupeKey && msg_raw?.timestamp && !seenMessageIdsRef.current.has(dedupeKey)) {
              seenMessageIdsRef.current.add(dedupeKey);

              if (seenMessageIdsRef.current.size > MAX_SEEN_MESSAGE_IDS) {
                const oldest = seenMessageIdsRef.current.values().next().value;
                seenMessageIdsRef.current.delete(oldest);
              }

              setMessages((messages) => [...messages, msg_raw]);
            }
          }

          if (msg_raw.from === "CREATE_TICKET_SERVICE") {
            msg_raw.socketType = "CREATE_TICKET";
            setNewTicket(msg_raw);
          }

          // ROTATOR
          if (Array.isArray(msg_raw) && msg_raw.length > 0 && msg_raw[0].from === "ROTATOR_CRON_SERVICE") {
            const uniqueMessages = Object.values(
              msg_raw.reduce((acc, msg) => {
                acc[msg.chatId] = acc[msg.chatId] || msg;
                return acc;
              }, {}),
            );
            setNewSales([...uniqueMessages]);
          }
        };

        const adminRoles = ["SUPER ADMIN", "ADMIN", "HEAD", "CS", "AUDITOR", "SYSTEM ADMIN"];

        socketIO.on("connect", async () => {
          setCookie("socketId", socketIO.id);
          setLivechatConnects((count) => count + 1);

          socketIO.emit(
            "join",
            JSON.stringify({
              id: cookies.id,
              admin: adminRoles.includes(cookies.role?.name),
            }),
          );

          // PEMBERITAHUAN ONLINE, KE SALURAN

          // Convert tokenAt to number and check if token is older than 50 minutes
          const loginAt = localStorage.getItem("loginAt") ?? null;
          const tokenTimestamp = loginAt ? parseInt(loginAt) : null;
          const fiftyMinutesAgo = Date.now() - 50 * 60 * 1000; // 50 minutes in milliseconds

          if (!loginAt || tokenTimestamp < fiftyMinutesAgo) {
            await servicePutUsersOnline(cookies.SIDikey, cookies.id, socketIO.id, cookies.departmentIds, "web_login");
            localStorage.setItem("loginAt", Date.now().toString());
          } else {
            const res = await servicePutUsersOnline(
              cookies.SIDikey,
              cookies.id,
              socketIO.id,
              cookies.departmentIds,
              "web_reload",
            );
            if (res.data.status === "OFFLINE") {
              await servicePutUsersOnline(cookies.SIDikey, cookies.id, socketIO.id, cookies.departmentIds, "web_login");
              localStorage.setItem("loginAt", Date.now().toString());
            } else {
              const res = await servicePutUsersOnline(
                cookies.SIDikey,
                cookies.id,
                socketIO.id,
                cookies.departmentIds,
                "web_reload",
              );
              if (res.data?.status === "OFFLINE") {
                await servicePutUsersOnline(
                  cookies.SIDikey,
                  cookies.id,
                  socketIO.id,
                  cookies.departmentIds,
                  "web_login",
                );
                localStorage.setItem("loginAt", Date.now().toString());
              }
            }

            socketIO.emit(
              "admin",
              JSON.stringify({
                sign: true,
                type: "signIn",
                id: cookies.id,
                firstName: cookies.firstName,
                lastName: cookies.lastName,
                status: "ONLINE",
              }),
            );
          }
        });

        // Set up reconnection logic
        const tryReconnect = () => {
          setTimeout(() => {
            socketIO.open((err) => {
              if (err) {
                tryReconnect();
              }
            });
          }, 2000);
        };

        socketIO.on("close", tryReconnect);

        // Set up appropriate listeners based on role
        if (adminRoles.includes(cookies.role?.name)) {
          socketIO.on("admin", socketListener);
        }
        // Every role gets personal, targeted events (e.g. unopened chat
        // summary — per-owner for SALES/CS, per-department for HEAD,
        // company-wide for ADMIN/SUPER ADMIN/AUDITOR/SYSTEM ADMIN) on its own
        // userId room, in addition to the shared "admin" room above. This was
        // previously whitelisted to ["SALES", "CS", "HEAD"], which silently
        // dropped every personal-room event the backend sends to the other
        // roles (OPI-444 fixed HEAD; ADMIN/SUPER ADMIN/AUDITOR/SYSTEM ADMIN
        // were still missing here even after the backend started targeting
        // them, since nothing was listening on their userId room).
        socketIO.on(cookies.id, socketListener);

        // Clean up function to disconnect and remove listeners when unmounting
        // or when user logs out (cookies.SIDikey changes to empty)
        return () => {
          socketIO.off("connect");
          socketIO.off("admin", socketListener);
          socketIO.off(cookies.id, socketListener);
          socketIO.off("close", tryReconnect);
          socketIO.disconnect();
          seenMessageIdsRef.current.clear();
        };
      }
    } catch (error) {
      console.error("Socket initialization failed:", error);
    }
  }

  async function initializeFirebaseToken() {
    if (roleFunctions.includes("read")) {
      const firebaseToken = localStorage.getItem("firebaseToken") ?? null;
      const tokenAt = localStorage.getItem("getFirebaseTokenAt") ?? null;

      // Convert tokenAt to number and check if token is older than 50 minutes
      const tokenTimestamp = tokenAt ? parseInt(tokenAt) : null;
      const fiftyMinutesAgo = Date.now() - 50 * 60 * 1000; // 50 minutes in milliseconds

      if (!firebaseToken || !tokenTimestamp || tokenTimestamp < fiftyMinutesAgo) {
        try {
          if (Notification.permission !== "granted") {
            await Notification.requestPermission();
          }
          const token = await requestForFirebaseToken();

          if (token) {
            const result = await subscribeFirebaseToken(cookies.SIDikey, token);
            localStorage.setItem("firebaseToken", token);
            localStorage.setItem("getFirebaseTokenAt", Date.now().toString());
            console.log("Token updated successfully", result);
          } else {
            console.warn("Failed to get Firebase token");
          }
        } catch (error) {
          console.error("Error initializing Firebase token:", error);
        }
      } else {
        console.log("Firebase token is still valid");
      }
    }
  }

  useEffect(() => {
    viewChange();
    initializeFirebaseToken();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Stops scrolling on overlay
  useLayoutEffect(() => {
    if (visibility) {
      document.body.style.overflow = "hidden";
      document.body.style.height = "100%";
    }
    if (!visibility) {
      document.body.style.overflow = "auto";
      document.body.style.height = "auto";
    }
  }, [visibility]);

  // function to toggle sidebar
  const toggleSidebar = (e) => {
    e.preventDefault();
    if (visibility === false) {
      setVisibility(true);
    } else {
      setVisibility(false);
    }
  };

  // function to change the design view under 1200 px
  const viewChange = () => {
    if (window.innerWidth < 1200) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setVisibility(false);
    }
  };
  window.addEventListener("load", viewChange);
  window.addEventListener("resize", viewChange);

  const sidebarClass = classNames({
    "nk-sidebar-mobile": mobileView,
    "nk-sidebar-active": visibility && mobileView,
  });

  function ToastDisplay() {
    return (
      <div className="d-flex align-items-center">
        <Icon name="bell toast-bell" />
        <div>
          <p>
            <b>{notification?.title}</b>
          </p>
          <p>{notification?.body}</p>
        </div>
      </div>
    );
  }

  const goToNotificationChat = (chatId, tab) => {
    if (chatId) {
      history.push(`${process.env.PUBLIC_URL}/chat-platform?chatID=${chatId}`);
    } else if (tab) {
      history.push(`${process.env.PUBLIC_URL}/chat-platform?tab=${tab}`);
    }
  };

  const notify = () =>
    toast(
      <ToastDisplay />,
      notification?.chatId || notification?.tab
        ? { onClick: () => goToNotificationChat(notification.chatId, notification.tab) }
        : undefined,
    );

  const lastNotifiedRef = useRef({ key: null, at: 0 });
  const DEDUPE_WINDOW_MS = 5000;

  useEffect(() => {
    if (!notification?.title) return;

    // Same event can arrive via both the socket channel and FCM push (foreground
    // tab) almost simultaneously — collapse those into a single toast.
    const key = `${notification.title}|${notification.body}`;
    const now = Date.now();

    if (lastNotifiedRef.current.key === key && now - lastNotifiedRef.current.at < DEDUPE_WINDOW_MS) {
      return;
    }

    lastNotifiedRef.current = { key, at: now };
    notify();
  }, [notification]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const unsubscribe = onMessageListener((payload) => {
      const title = payload?.notification?.title ?? payload?.data?.title;
      const body = payload?.notification?.body ?? payload?.data?.body;
      const chatId = payload?.data?.chat_id;
      const tab = !chatId && title?.startsWith("Unopened Chat Summary") ? "unopened" : undefined;
      setNotification({ title, body, chatId, tab });
      if (title && Notification.permission === "granted") {
        const browserNotif = new Notification(title, { body });
        browserNotif.onclick = () => {
          window.focus();
          goToNotificationChat(chatId, tab);
          browserNotif.close();
        };
      }
    });

    return () => unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MessagesContext.Provider
      value={{
        messagesValue: [messages, setMessages],
        statusValue: [statusMessages, setStatusMessages],
        onlineValue: [statusOnlineUsers, setStatusOnlineUsers],
        salesUpdate: [newSales],
        ticketUpdate: [newTicket],
        livechatValue: [livechatEvents, livechatConnects],
      }}
    >
      <ToastContainer theme="colored" position="top-right" toastStyle={{ backgroundColor: "#f4bd0e" }} />
      <React.Fragment>
        <Head title="Loading" />
        <div className="nk-app-root">
          <div className="nk-main">
            <Sidebar
              sidebarToggle={toggleSidebar}
              fixed
              mobileView={mobileView}
              theme={themeState.sidebar}
              className={sidebarClass}
              setVisibility={setVisibility}
            />
            {visibility && mobileView && <div className="nk-sidebar-overlay" onClick={toggleSidebar}></div>}
            <div className="nk-wrap">
              <Header sidebarToggle={toggleSidebar} fixed setVisibility={setVisibility} theme={themeState.header} />

              <Pages />
              <Footer />
            </div>
          </div>
        </div>
      </React.Fragment>
    </MessagesContext.Provider>
  );
};
export default Layout;
